import type { LucideIcon } from 'lucide-react';
import {
  LayoutGrid,
  Eye,
  Gauge,
  GitBranch,
  Rocket,
} from 'lucide-react';

/** How well a side covers a dimension: yes = strong/built-in, partial = limited or build-it-yourself, no = not available. */
export type ComparisonLevel = 'yes' | 'partial' | 'no';

export interface ComparisonDimension {
  dimension: string;
  raisyLevel: ComparisonLevel;
  competitorLevel: ComparisonLevel;
  raisy: string;
  competitor: string;
}

export interface DeepDive {
  icon: LucideIcon;
  title: string;
  raisy: string;
  competitor: string;
}

export interface ComparisonFaq {
  question: string;
  answer: string;
}

export interface Comparison {
  slug: string;
  competitorName: string;
  badge: string;
  headline: string;
  subheadline: string;
  seo: {
    title: string;
    description: string;
    keywords: string;
    canonicalPath: string;
  };
  dimensions: ComparisonDimension[];
  deepDives: DeepDive[];
  competitorGoodFit: string[];
  raisyGoodFit: string[];
  faqs: ComparisonFaq[];
  lastUpdated: string;
}

export const comparisons: Comparison[] = [
  {
    slug: 'raisy-vs-claude',
    competitorName: 'Claude',
    badge: 'Model vs. Workforce Operating System',
    headline: 'Raisy vs Claude: the engine vs. the vehicle',
    subheadline:
      'Claude is a powerful large language model from Anthropic. Raisy is the governed operating layer that turns models like Claude into a managed AI workforce — with structure, transparency, KPIs, and memory. They are not the same category of thing, and Raisy can even run on top of Claude.',
    seo: {
      title: 'Raisy vs Claude: AI Workforce OS vs Model API',
      description:
        'Claude is Anthropic\'s LLM. Raisy is the AI workforce operating system that adds structure, transparency, KPIs, self-learning memory, and easy setup on top of models like Claude.',
      keywords:
        'raisy vs claude, claude alternative, anthropic claude comparison, AI workforce platform, AI agent orchestration, LLM transparency, AI KPIs, AI operating system',
      canonicalPath: '/compare/raisy-vs-claude',
    },
    dimensions: [
      {
        dimension: 'What it is',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'An AI workforce operating system: entities, processes, bots, and kernels organized in a 4-level management matrix',
        competitor:
          'A large language model and API from Anthropic — raw intelligence you call from your own code',
      },
      {
        dimension: 'Transparency & audit',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Every LLM call is logged with exact kernel versions, inputs, outputs, and token usage. There are no invisible LLM calls. ISO 9001 auditability by design',
        competitor:
          'The API returns outputs; logging, tracing, and audit trails are yours to build',
      },
      {
        dimension: 'KPIs & measurement',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Built-in KPIs at every level — entity, process, and bot — computed deterministically by code, never estimated by the LLM',
        competitor:
          'No built-in business KPIs; you define and compute your own metrics',
      },
      {
        dimension: 'Memory & learning',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Kernels are immutable and versioned. Feedback creates a new version; the old one is never deleted. The system learns without forgetting',
        competitor:
          'Stateless per request by default; memory and learning loops are custom engineering',
      },
      {
        dimension: 'Human oversight',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Confidence-based autonomy: bots start with mandatory human review and graduate to full autonomy as their approval rate climbs',
        competitor:
          'No approval gates built in; you implement your own review workflow',
      },
      {
        dimension: 'Setup effort',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Paste your company URL — Raisy drafts the entity, its processes, and its bots automatically. You review and approve',
        competitor:
          'Start from an API key and a prompt; everything above that is your build',
      },
      {
        dimension: 'Versioning & reproducibility',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'An assembly hash locks the exact kernel versions of every run — two executions with the same hash ran identical logic',
        competitor:
          'Model versions change over time; prompt and logic versioning is up to you',
      },
      {
        dimension: 'Model lock-in',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Model-agnostic runtime — currently Google Gemini and OpenAI, with Claude pluggable as an engine per bot',
        competitor: 'Anthropic models only',
      },
      {
        dimension: 'Pricing model',
        raisyLevel: 'partial',
        competitorLevel: 'yes',
        raisy:
          'A subscription for a managed AI workforce — worth it against the cost of hiring, not the cost of tokens',
        competitor: 'Usage-based API pricing per token — cheap for single features and prototypes',
      },
      {
        dimension: 'Best for',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'Companies that want AI to run business processes — sales, recruiting, fundraising, operations — with oversight and accountability',
        competitor:
          'Developers building custom AI features who want full control of their own orchestration code',
      },
    ],
    deepDives: [
      {
        icon: LayoutGrid,
        title: 'An intuitive, layered map of everything your AI does',
        raisy:
          'Raisy organizes work in a 4-level matrix: Entities (who acts) contain Processes (what they do), which are executed by Bots, which are powered by Kernels (what they know). Every layer is visualized interactively — entity hierarchies, process builders, automation flows — so you can see your entire AI workforce as a living org chart and drill into any node.',
        competitor:
          'Claude gives you a conversation or an API response. If you chain fifty prompts into a business process, the structure of that system lives in your code and your head — there is no built-in visual layer for entities, processes, or agents.',
      },
      {
        icon: Eye,
        title: 'Radical transparency: no invisible LLM calls',
        raisy:
          'Every single LLM call inside Raisy is logged with the exact kernel versions used, the full input, the full output, and token consumption. Outputs are translated back into your business language, because a system you cannot inspect is a black box. Auditability is a design principle, not an add-on.',
        competitor:
          'Claude is a black box by nature: you send a prompt, you get text. Anthropic does not expose an audit trail of your orchestration — which prompt version produced which business decision is invisible unless you build that logging yourself.',
      },
      {
        icon: Gauge,
        title: 'KPIs that quantify every entity, process, and bot',
        raisy:
          'Every level of the Raisy hierarchy carries its own KPIs, measured against previous runs, so performance is a number — not a vibe. Crucially, KPIs are computed by a deterministic code evaluator, never by the LLM itself: LLMs produce language, they do not do maths. This is how bots earn autonomy: confidence = approvals ÷ runs, tracked over time.',
        competitor:
          'Claude can write a report about performance, but it cannot be the scorekeeper of its own reliability. There is no built-in concept of per-agent KPIs, approval rates, or performance trends.',
      },
      {
        icon: GitBranch,
        title: 'Self-learning transparency — without forgetting',
        raisy:
          'When you reject a bot\'s output in Raisy, that feedback triggers a kernel patch, which is quality-gated and stored as a new version. The old version is never deleted — it is the audit trail. The system provably improves with every correction while keeping a complete, inspectable history of what it knew and when.',
        competitor:
          'Claude does not learn from your corrections between sessions. Each conversation starts fresh, and fine-tuning or prompt updates are manual, opaque, and easy to regress — improving the system risks losing what previously worked.',
      },
      {
        icon: Rocket,
        title: 'Setup made easy: from one URL to a running workforce',
        raisy:
          'Onboarding in Raisy starts with your company name and website. Raisy drafts a classified entity, discovers the domain, proposes processes, and assembles the bots to run them — no prompt engineering, no orchestration code. Humans stay in the loop through review, not through construction.',
        competitor:
          'With the Claude API, setup means hiring engineers: designing prompts, building agent loops, wiring tools, adding logging, and maintaining it all. Powerful, but it is a software project, not an onboarding flow.',
      },
    ],
    competitorGoodFit: [
      'You have a development team and want full control over your own AI orchestration code',
      'You are building a single, well-defined AI feature inside your own product (chat, summarization, classification)',
      'You are prototyping or experimenting and want raw model access with usage-based pricing',
      'You specifically need Anthropic\'s model capabilities and are happy to build everything around them yourself',
    ],
    raisyGoodFit: [
      'You want AI to run business processes — sales, recruiting, fundraising, operations — not just answer prompts',
      'You need a complete audit trail of every AI decision, call, and version for compliance or accountability',
      'You want KPIs and confidence scores that quantify how every bot and process is actually performing',
      'You want a system that learns from every correction without ever losing its history',
      'You want to start from your company URL, not from an API key and an engineering roadmap',
    ],
    faqs: [
      {
        question: 'Is Raisy a replacement for Claude?',
        answer:
          'No — they are different categories. Claude is a large language model (the engine). Raisy is an AI workforce operating system (the vehicle) that adds structure, oversight, KPIs, audit trails, and memory on top of LLMs. Raisy is model-agnostic and can use Claude as one of its engines.',
      },
      {
        question: 'Does Raisy use Claude under the hood?',
        answer:
          'Raisy\'s runtime is model-agnostic and currently runs on Google Gemini and OpenAI models. Claude was one of the original target models in Raisy\'s architecture, and the runner treats the model as a swappable parameter per bot — so Claude can be plugged in where it is the best fit.',
      },
      {
        question: 'What does Raisy add that the Claude API does not?',
        answer:
          'A management layer: entities, processes, and bots organized in a visual hierarchy; a complete log of every LLM call with exact versions; deterministic KPIs at every level; confidence-based human approval gates; and versioned, immutable memory that lets the system learn from feedback without forgetting.',
      },
      {
        question: 'Which is cheaper — Raisy or Claude?',
        answer:
          'They price different things. Claude charges per token of API usage — cheapest for a single feature or prototype. Raisy is a subscription for a managed AI workforce, positioned against the cost of hiring people to run those processes. For running business operations, compare Raisy to payroll, not to token bills.',
      },
      {
        question: 'Can I switch models with Raisy?',
        answer:
          'Yes. In Raisy the model is a parameter of each bot, not a marriage. You can run different bots on different models — fast modes on lightweight models, quality modes on frontier models — and swap providers without rebuilding your processes.',
      },
    ],
    lastUpdated: 'July 28, 2026',
  },
  {
    slug: 'raisy-vs-chatgpt',
    competitorName: 'ChatGPT',
    badge: 'Assistant vs. AI Workforce',
    headline: 'Raisy vs ChatGPT: a chat assistant vs. a workforce that operates',
    subheadline:
      'ChatGPT is OpenAI\'s general-purpose AI assistant — excellent at conversation, drafts, and answers. Raisy is an AI workforce operating system that turns AI into managed entities, processes, and bots that run your business operations with oversight, KPIs, and memory.',
    seo: {
      title: 'Raisy vs ChatGPT: AI Workforce OS vs AI Assistant',
      description:
        'ChatGPT answers. Raisy operates. Compare OpenAI\'s assistant with Raisy\'s managed AI workforce: structure, transparency, KPIs, self-learning memory, and setup from one URL.',
      keywords:
        'raisy vs chatgpt, chatgpt alternative, openai chatgpt comparison, AI workforce platform, AI agents for business, chatgpt for business operations',
      canonicalPath: '/compare/raisy-vs-chatgpt',
    },
    dimensions: [
      {
        dimension: 'What it is',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'An AI workforce operating system: entities, processes, bots, and kernels organized in a 4-level management matrix',
        competitor:
          'A general-purpose AI assistant from OpenAI — a conversational interface to powerful models',
      },
      {
        dimension: 'Transparency & audit',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Every LLM call is logged with exact kernel versions, inputs, outputs, and token usage. There are no invisible LLM calls',
        competitor:
          'Conversations are visible and Enterprise tiers add admin controls, but business processes built around ChatGPT have no built-in audit trail',
      },
      {
        dimension: 'KPIs & measurement',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Built-in KPIs at entity, process, and bot level — computed deterministically by code, never estimated by the LLM',
        competitor:
          'No concept of per-agent performance metrics; it can discuss KPIs but not be measured by them',
      },
      {
        dimension: 'Memory & learning',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Kernels are immutable and versioned; feedback creates a new version, the old one is never deleted. Learning without forgetting',
        competitor:
          'Memory features remember preferences, but there is no versioned, auditable improvement loop for business logic',
      },
      {
        dimension: 'Human oversight',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Confidence-based autonomy: bots start with mandatory human review and graduate as their approval rate climbs',
        competitor:
          'The human is always in the loop — because the human IS the loop. Every action is manual copy-paste',
      },
      {
        dimension: 'Setup effort',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Paste your company URL — Raisy drafts the entity, its processes, and its bots automatically',
        competitor:
          'Start chatting immediately — but turning chats into business processes is entirely on you',
      },
      {
        dimension: 'Execution',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Bots execute multi-step processes across email, phone, LinkedIn, and messaging platforms, 24/7',
        competitor:
          'Produces text and artifacts inside the chat (agent features are emerging); acting on the outside world still requires you or custom builds',
      },
      {
        dimension: 'Model lock-in',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Model-agnostic runtime — currently Google Gemini and OpenAI, swappable per bot',
        competitor: 'OpenAI models only',
      },
      {
        dimension: 'Pricing model',
        raisyLevel: 'partial',
        competitorLevel: 'yes',
        raisy:
          'A subscription for a managed AI workforce — worth it against the cost of hiring, not the cost of seats',
        competitor: 'Per-seat subscription (Free / Plus / Team / Enterprise) — cheap for assisting people',
      },
      {
        dimension: 'Best for',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'Companies that want AI to run business processes — sales, recruiting, fundraising, operations — with accountability',
        competitor:
          'Individuals and teams who want a brilliant assistant for writing, analysis, coding help, and brainstorming',
      },
    ],
    deepDives: [
      {
        icon: LayoutGrid,
        title: 'A managed structure, not a chat window',
        raisy:
          'Raisy organizes AI work the way companies organize human work: Entities contain Processes, executed by Bots, powered by Kernels — all visualized as an interactive hierarchy you can inspect and manage. Your AI workforce has an org chart.',
        competitor:
          'ChatGPT is a flat conversation list. Projects and custom GPTs help organize chats, but there is no structural model of your company, its processes, or who (which agent) does what.',
      },
      {
        icon: Eye,
        title: 'Radical transparency: no invisible LLM calls',
        raisy:
          'Every LLM call inside Raisy is logged with exact kernel versions, full input, full output, and token consumption. When a bot made a business decision, you can replay exactly why.',
        competitor:
          'ChatGPT shows you the conversation, but the moment AI becomes part of a real process — via the API, GPTs, or copy-paste — the audit trail of what ran, with which instructions, disappears.',
      },
      {
        icon: Gauge,
        title: 'KPIs that quantify every bot and process',
        raisy:
          'Every level of Raisy carries KPIs measured against previous runs, computed by deterministic code — never by the LLM itself. Bots earn autonomy through a tracked approval rate, like employees earning trust.',
        competitor:
          'ChatGPT can write you a KPI report, but it cannot be the subject of one. There are no per-agent approval rates, no performance trends, no graduation from supervised to autonomous.',
      },
      {
        icon: GitBranch,
        title: 'Self-learning transparency — without forgetting',
        raisy:
          'Reject a bot\'s output and Raisy patches the underlying kernel, quality-gates it, and stores it as a new version. The old version is never deleted — improvement and audit trail are the same mechanism.',
        competitor:
          'Correcting ChatGPT improves the current conversation at best. There is no versioned history of what your "AI employee" knew last month, and no guarantee a correction survives into next week\'s session.',
      },
      {
        icon: Rocket,
        title: 'Setup made easy: from one URL to a running workforce',
        raisy:
          'Onboarding starts with your company name and website. Raisy drafts a classified entity, proposes processes, and assembles the bots to run them. You review and approve — you don\'t construct.',
        competitor:
          'ChatGPT starts fast for questions, but there is no onboarding path from "chatting" to "running my operations" — that gap is filled by your own manual work or engineering.',
      },
    ],
    competitorGoodFit: [
      'You need a versatile assistant for writing, analysis, brainstorming, or coding help',
      'You are an individual or small team happy to drive every AI interaction manually',
      'You want to prototype an idea with a conversation before building anything',
      'You need broad, general-purpose intelligence at consumer pricing',
    ],
    raisyGoodFit: [
      'You want AI to run business processes — sales, recruiting, fundraising, operations — not just discuss them',
      'You need an audit trail of every AI decision and the exact instructions behind it',
      'You want KPIs and confidence scores proving how each bot and process performs',
      'You want corrections to permanently improve the system without losing history',
      'You want to onboard with your company URL, not with prompt engineering',
    ],
    faqs: [
      {
        question: 'Is Raisy just a wrapper around ChatGPT?',
        answer:
          'No. Raisy is a governed runtime that is model-agnostic — it currently uses Google Gemini and OpenAI models as engines, and adds what no chat assistant has: entities, processes, and bots in a managed hierarchy; audit logs of every call; deterministic KPIs; confidence-based approval gates; and versioned, self-improving memory.',
      },
      {
        question: 'Does Raisy use OpenAI models?',
        answer:
          'Yes — OpenAI models (including GPT-4.1) are among the engines Raisy can run on, alongside Google Gemini. In Raisy, the model is a swappable parameter of each bot, not the product itself.',
      },
      {
        question: 'What about ChatGPT Team or Enterprise for business use?',
        answer:
          'ChatGPT Team/Enterprise gives your employees a shared, secure assistant — every interaction is still human-driven. Raisy is different: it gives you AI employees that execute processes autonomously (with approval gates), report KPIs, and improve from feedback. One assists your team; the other is a team.',
      },
      {
        question: 'Can custom GPTs or agents replace Raisy?',
        answer:
          'Custom GPTs package a prompt and some tools for manual use. They have no management layer: no KPIs, no approval-rate-based autonomy, no versioned kernels, no cross-process audit trail. Raisy is the management layer those building blocks lack.',
      },
      {
        question: 'Which is cheaper — Raisy or ChatGPT?',
        answer:
          'ChatGPT is far cheaper for assisting a person (per-seat pricing). Raisy is priced against staffing, not seats: it takes over the toil in your sales, recruiting, or operations processes while your people keep the decisions. Compare Raisy to the cost of staffing those functions, not to a Plus subscription.',
      },
    ],
    lastUpdated: 'July 28, 2026',
  },
  {
    slug: 'raisy-vs-zapier',
    competitorName: 'Zapier',
    badge: 'Workflow Automation vs. AI Workforce',
    headline: 'Raisy vs Zapier: connecting apps vs. running the company',
    subheadline:
      'Zapier connects 7,000+ apps with trigger-action workflows — the standard for deterministic automation. Raisy is an AI workforce operating system that builds and runs entire business processes with reasoning bots, KPIs, and human oversight.',
    seo: {
      title: 'Raisy vs Zapier: AI Workforce vs App Automation',
      description:
        'Zapier connects apps with zaps. Raisy runs business processes with a managed AI workforce. Compare automation vs operation: KPIs, oversight, self-learning, and setup effort.',
      keywords:
        'raisy vs zapier, zapier alternative, AI automation comparison, AI workforce platform, business process automation, zapier AI agents',
      canonicalPath: '/compare/raisy-vs-zapier',
    },
    dimensions: [
      {
        dimension: 'What it is',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'An AI workforce operating system: entities, processes, bots, and kernels in a 4-level management matrix',
        competitor:
          'An automation platform connecting apps via trigger-action "Zaps" — the category leader in iPaaS',
      },
      {
        dimension: 'How work gets defined',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Backward construction: declare the outcome, Raisy derives the process chain and assembles the bots',
        competitor:
          'You design every workflow step by step, mapping triggers to actions across apps',
      },
      {
        dimension: 'Reasoning vs. rules',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Bots reason over context — they write, decide, and adapt using LLM kernels with human approval gates',
        competitor:
          'Zaps follow fixed rules — excellent for deterministic handoffs, not for judgment calls (AI steps are add-ons you wire in)',
      },
      {
        dimension: 'Transparency & audit',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Every LLM call logged with exact kernel versions, inputs, outputs, and tokens. No invisible LLM calls',
        competitor:
          'Task history shows what ran, but AI steps are black boxes — prompts, versions, and reasoning are not audited',
      },
      {
        dimension: 'KPIs & measurement',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Built-in KPIs at entity, process, and bot level, computed deterministically by code — never by the LLM',
        competitor:
          'Task counts and error rates, but no native business KPIs or per-agent performance measurement',
      },
      {
        dimension: 'Memory & learning',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Feedback patches kernels into new immutable versions — the system learns from corrections and never forgets',
        competitor:
          'Workflows are static until you edit them; a Zap does not improve because you fixed its output',
      },
      {
        dimension: 'Human oversight',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Confidence-based autonomy: bots graduate from mandatory review to full autonomy as approval rates climb',
        competitor:
          'Approval steps can be wired in, but there is no trust model deciding when review is no longer needed',
      },
      {
        dimension: 'Setup effort',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Paste your company URL — entity, processes, and bots are drafted for you to review',
        competitor:
          'Connect apps, then build each Zap yourself — fast for simple ones, a project for real processes',
      },
      {
        dimension: 'App coverage',
        raisyLevel: 'partial',
        competitorLevel: 'yes',
        raisy:
          'Focused execution channels (email, phone, LinkedIn, messaging) plus a growing skill ecosystem',
        competitor:
          '7,000+ app integrations — the broadest catalog in the industry, a genuine strength',
      },
      {
        dimension: 'Best for',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'Companies that want AI to run judgment-heavy processes — sales, recruiting, fundraising, operations',
        competitor:
          'Teams that need reliable, deterministic handoffs between SaaS apps (form → CRM → Slack)',
      },
    ],
    deepDives: [
      {
        icon: LayoutGrid,
        title: 'A company map, not a list of Zaps',
        raisy:
          'Raisy organizes work as Entities, Processes, Bots, and Kernels in a visual hierarchy. You manage a workforce: every process has an owner, every bot a job description, every kernel a known skill.',
        competitor:
          'Zapier organizes work as a flat list of Zaps. For dozens of app handoffs that is fine; for running a business function, the structure of "who does what and why" lives outside the tool.',
      },
      {
        icon: Eye,
        title: 'Radical transparency: no invisible LLM calls',
        raisy:
          'Every LLM call in Raisy is logged with exact kernel versions, full input, full output, and token usage — ISO 9001-style auditability by design.',
        competitor:
          'Zapier logs task runs faithfully, but its AI steps don\'t give you a versioned audit of prompts and reasoning. When an AI-generated email went out, reconstructing exactly why is on you.',
      },
      {
        icon: Gauge,
        title: 'KPIs that quantify every entity, process, and bot',
        raisy:
          'Raisy computes KPIs at every level with a deterministic code evaluator — LLMs produce language, they do not do maths. Bots earn autonomy through measured approval rates.',
        competitor:
          'Zapier measures task throughput and errors, not business outcomes. There is no concept of a Zap\'s "performance review" or a bot earning more responsibility.',
      },
      {
        icon: GitBranch,
        title: 'Self-learning transparency — without forgetting',
        raisy:
          'Reject a bot\'s output and Raisy patches the kernel, quality-gates it, and stores a new immutable version. One correction improves every bot that shares that capability — and the old version stays as the audit trail.',
        competitor:
          'A Zap is exactly as smart as the day you built it. Improving it means editing it yourself, and there is no versioned record of what changed in its AI behavior over time.',
      },
      {
        icon: Rocket,
        title: 'Setup made easy: from one URL to a running workforce',
        raisy:
          'Raisy starts from your company website and drafts the entity, processes, and bots. You approve; you don\'t wire.',
        competitor:
          'Zapier starts from a blank canvas. Its builder is genuinely excellent for connecting two apps — but assembling an end-to-end business process is your design work, Zap by Zap.',
      },
    ],
    competitorGoodFit: [
      'You need deterministic handoffs between SaaS apps — form submissions, notifications, data syncs',
      'You want the broadest possible app catalog (7,000+ integrations)',
      'Your workflows are simple, rules-based, and shouldn\'t involve AI judgment',
      'You want cheap, proven automation for a handful of well-defined tasks',
    ],
    raisyGoodFit: [
      'Your processes require judgment — writing personalized outreach, qualifying leads, deciding next steps',
      'You want AI to run a business function end-to-end, not shuttle data between apps',
      'You need KPIs, approval gates, and an audit trail for every AI decision',
      'You want the system to learn from your corrections without losing history',
      'You want to start from your company URL, not from a workflow canvas',
    ],
    faqs: [
      {
        question: 'Is Raisy a Zapier replacement?',
        answer:
          'They solve different problems. Zapier moves data between apps with fixed rules — best in class at it. Raisy runs judgment-heavy business processes with managed AI bots that reason, get reviewed, report KPIs, and improve. Many companies would use Zapier for plumbing and Raisy for operations.',
      },
      {
        question: 'Does Raisy connect to as many apps as Zapier?',
        answer:
          'No — and we won\'t pretend otherwise. Zapier\'s 7,000+ app catalog is a genuine strength. Raisy focuses on the execution channels that run business development and operations (email, phone, LinkedIn, messaging) plus a growing skill ecosystem, rather than long-tail app sync.',
      },
      {
        question: 'Can\'t I just add AI steps to my Zaps?',
        answer:
          'You can — and then you own everything Raisy provides out of the box: prompt versioning, per-bot KPIs, approval gates, confidence-based autonomy, and an audit trail of every AI call. AI steps make a Zap smarter; they don\'t make it a managed workforce.',
      },
      {
        question: 'Which is cheaper — Raisy or Zapier?',
        answer:
          'Zapier is cheaper for task automation (per-task pricing). Raisy is priced against hiring people to run your sales, recruiting, or operations processes. If your alternative is a Zap, Zapier wins. If your alternative is a hire, Raisy wins.',
      },
      {
        question: 'Does Raisy use Zapier internally?',
        answer:
          'Raisy uses workflow engines (including n8n) as execution plumbing under the hood, and adds the layer those engines lack: the entities, processes, bots, kernels, KPIs, oversight, and memory that turn workflows into a managed workforce.',
      },
    ],
    lastUpdated: 'July 28, 2026',
  },
  {
    slug: 'raisy-vs-n8n',
    competitorName: 'n8n',
    badge: 'Workflow Engine vs. Company OS',
    headline: 'Raisy vs n8n: building workflows vs. building the company',
    subheadline:
      'n8n is a powerful, self-hostable workflow engine beloved by technical teams — Raisy uses workflow engines like it under the hood. Raisy is the layer above: an AI workforce operating system that builds and manages the processes for you.',
    seo: {
      title: 'Raisy vs n8n: AI Workforce OS vs Workflow Engine',
      description:
        'n8n runs workflows. Raisy builds companies. Compare the self-hosted workflow engine with Raisy\'s managed AI workforce: who builds, who manages, KPIs, oversight, and setup effort.',
      keywords:
        'raisy vs n8n, n8n alternative, workflow automation comparison, AI workforce platform, n8n AI agents, business process automation',
      canonicalPath: '/compare/raisy-vs-n8n',
    },
    dimensions: [
      {
        dimension: 'What it is',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'An AI workforce operating system: entities, processes, bots, and kernels in a managed 4-level matrix',
        competitor:
          'An open, self-hostable workflow automation engine — a developer-friendly toolbox for building integrations',
      },
      {
        dimension: 'Who builds the workflows',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Raisy does: declare the outcome (or paste your URL) and processes are derived and assembled for your review',
        competitor:
          'You do: n8n gives you a canvas and 400+ nodes; every workflow is your design and your maintenance',
      },
      {
        dimension: 'Who it\'s for',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'Founders and operators who want business outcomes without building or maintaining automation',
        competitor:
          'Technical teams and tinkerers who want full control and enjoy building workflows',
      },
      {
        dimension: 'Transparency & audit',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Every LLM call logged with exact kernel versions, inputs, outputs, and tokens. No invisible LLM calls',
        competitor:
          'Execution logs per workflow, but AI node prompts, versions, and reasoning are not governed or audited',
      },
      {
        dimension: 'KPIs & measurement',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Built-in KPIs at entity, process, and bot level, computed deterministically by code',
        competitor:
          'Execution success/failure metrics only; no business KPIs or per-agent performance tracking',
      },
      {
        dimension: 'Memory & learning',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Feedback creates new immutable kernel versions — the system learns from corrections, old versions never deleted',
        competitor:
          'Workflows are static until edited; no learning loop, no versioned AI behavior history',
      },
      {
        dimension: 'Human oversight',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Confidence-based autonomy: bots graduate from mandatory review to autonomy as approval rates climb',
        competitor:
          'Manual gates can be wired as workflow steps, but there is no trust model deciding when they\'re no longer needed',
      },
      {
        dimension: 'Cost model',
        raisyLevel: 'partial',
        competitorLevel: 'yes',
        raisy:
          'Subscription for a managed AI workforce — priced against hiring, not against workflow runs',
        competitor:
          'Open-source/self-hosted (infrastructure costs) or cloud plans per execution — very cheap if you have the skills',
      },
      {
        dimension: 'Setup effort',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Paste your company URL — entity, processes, and bots drafted automatically',
        competitor:
          'Install, connect credentials, build each workflow node by node',
      },
      {
        dimension: 'Best for',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'Companies that want AI to run business processes — sales, recruiting, fundraising, operations — with oversight',
        competitor:
          'Engineers and technical operators building custom integrations who want full, self-hosted control',
      },
    ],
    deepDives: [
      {
        icon: LayoutGrid,
        title: 'A managed workforce, not a workflow canvas',
        raisy:
          'Raisy gives you an org chart for AI: Entities own Processes, Bots execute them, Kernels define what they know — all visual, all managed, all with owners and job descriptions.',
        competitor:
          'n8n gives you a superb canvas for wiring nodes. But a canvas is not a management layer: there is no org structure, no per-agent accountability, no view of "who does what in my company."',
      },
      {
        icon: Eye,
        title: 'Radical transparency: no invisible LLM calls',
        raisy:
          'Every LLM call in Raisy is logged with exact kernel versions, full input, output, and tokens — auditable to ISO 9001 standards by design.',
        competitor:
          'n8n logs executions, but when an AI node produced a bad output, the prompt version, model, and reasoning behind it are not governed — you reconstruct, or you shrug.',
      },
      {
        icon: Gauge,
        title: 'KPIs that quantify every entity, process, and bot',
        raisy:
          'Raisy measures business outcomes per entity, process, and bot with a deterministic evaluator — never letting the LLM grade its own homework — and uses approval rates to graduate bots toward autonomy.',
        competitor:
          'n8n tells you a workflow ran or failed. Whether the process is actually getting better at its job is a dashboard you would have to build yourself.',
      },
      {
        icon: GitBranch,
        title: 'Self-learning transparency — without forgetting',
        raisy:
          'Every rejection patches a kernel into a new immutable version, quality-gated before it ships. One correction improves every bot sharing that capability; history is never overwritten.',
        competitor:
          'Improving an n8n workflow means editing it — and the old AI behavior is gone the moment you save. No learning loop, no versioned improvement trail.',
      },
      {
        icon: Rocket,
        title: 'Setup made easy: from one URL to a running workforce',
        raisy:
          'Raisy drafts your entity, processes, and bots from your company website. You review and approve — no canvas, no nodes, no credentials plumbing.',
        competitor:
          'n8n setup is honest engineering work: self-host or sign up, connect every credential, design every workflow. Powerful — if building workflows is how you want to spend your time.',
      },
    ],
    competitorGoodFit: [
      'You have technical people who enjoy building and maintaining workflows',
      'You need deep, custom integrations with full control over every node',
      'You want open-source, self-hosted automation at minimal licensing cost',
      'Your use case is deterministic data movement, not AI judgment',
    ],
    raisyGoodFit: [
      'You want business outcomes (pipeline, hires, investor meetings), not a workflow-building hobby',
      'You need KPIs, approval gates, and audit trails for AI decisions',
      'You want the system to improve from every correction without losing history',
      'You want setup to start from your company URL, not from a blank canvas',
      'You\'d rather manage an AI workforce than maintain automation infrastructure',
    ],
    faqs: [
      {
        question: 'Is Raisy built on n8n?',
        answer:
          'Raisy uses workflow engines — including n8n — as execution plumbing under the hood, and adds the layer above them: entities, processes, bots, and kernels with KPIs, approval gates, versioned memory, and a management interface. n8n runs workflows; Raisy builds and manages the company those workflows serve.',
      },
      {
        question: 'n8n is free and open-source. Why pay for Raisy?',
        answer:
          'n8n\'s license is cheap; building and maintaining the workflows is not. Raisy is priced against hiring people to run your processes, not against workflow software. If you have engineers and time, n8n is a great choice. If you want outcomes without construction, that\'s Raisy.',
      },
      {
        question: 'Can n8n\'s AI nodes do what Raisy does?',
        answer:
          'n8n\'s AI nodes can call LLMs inside workflows — but you still design, version, monitor, and improve everything yourself. There is no KPI layer, no confidence-based autonomy, no versioned kernel memory, no audit trail of AI reasoning. Raisy is that missing management layer.',
      },
      {
        question: 'Which is better for non-technical founders?',
        answer:
          'Raisy, by design. n8n is built for technical users who think in nodes and credentials. Raisy is built for founders who think in outcomes: paste your URL, review the drafted workforce, approve what goes out the door.',
      },
      {
        question: 'Can I keep my existing n8n workflows and still use Raisy?',
        answer:
          'Yes. Because Raisy can orchestrate workflow engines, existing n8n workflows can be wrapped as skills inside Raisy processes — gaining KPIs, oversight, and audit without a rebuild.',
      },
    ],
    lastUpdated: 'July 28, 2026',
  },
  {
    slug: 'raisy-vs-crewai',
    competitorName: 'crewAI',
    badge: 'Agent Framework vs. Finished Workforce',
    headline: 'Raisy vs crewAI: a framework for developers vs. a workforce for businesses',
    subheadline:
      'crewAI is a popular open-source Python framework for building multi-agent systems — excellent building blocks for engineers. Raisy is the finished product: a managed AI workforce with structure, KPIs, oversight, and memory, no engineering required.',
    seo: {
      title: 'Raisy vs crewAI: Managed AI Workforce vs Framework',
      description:
        'crewAI gives developers agent building blocks. Raisy gives businesses a managed AI workforce. Compare framework vs finished product: governance, KPIs, memory, and time to value.',
      keywords:
        'raisy vs crewai, crewai alternative, ai agent framework comparison, multi-agent platform, AI workforce platform, langchain alternative',
      canonicalPath: '/compare/raisy-vs-crewai',
    },
    dimensions: [
      {
        dimension: 'What it is',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'A finished AI workforce operating system: entities, processes, bots, and kernels, managed and visualized',
        competitor:
          'An open-source Python framework for orchestrating multi-agent systems — building blocks, not a product',
      },
      {
        dimension: 'Who it\'s for',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'Founders and operators who want AI running business processes without hiring engineers',
        competitor:
          'Developers building custom agent applications in Python',
      },
      {
        dimension: 'Time to value',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Paste your company URL — a drafted workforce to review within a session',
        competitor:
          'Weeks to months: design agents, write orchestration code, build tooling, deploy, maintain',
      },
      {
        dimension: 'Governance & audit',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Every LLM call logged with exact kernel versions, inputs, outputs, tokens. Approval gates with confidence-based autonomy',
        competitor:
          'Observability hooks and third-party tools exist, but logging, approvals, and compliance are your engineering problem',
      },
      {
        dimension: 'KPIs & measurement',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Built-in KPIs at entity, process, and bot level, computed deterministically by code — never by the LLM',
        competitor:
          'No KPI concept; you instrument your agents yourself',
      },
      {
        dimension: 'Memory & learning',
        raisyLevel: 'yes',
        competitorLevel: 'partial',
        raisy:
          'Immutable versioned kernels: feedback creates new versions, old ones never deleted. Learning without forgetting',
        competitor:
          'Memory primitives exist, but versioned, auditable improvement loops are custom work',
      },
      {
        dimension: 'Management UI',
        raisyLevel: 'yes',
        competitorLevel: 'no',
        raisy:
          'Full management interface: entity hierarchies, process builders, feedback queues, KPI dashboards',
        competitor:
          'A code library; any UI is yours to build',
      },
      {
        dimension: 'Control & flexibility',
        raisyLevel: 'partial',
        competitorLevel: 'yes',
        raisy:
          'Opinionated structure — you configure and govern, the platform runs',
        competitor:
          'Total code-level control — anything is possible, everything is your responsibility',
      },
      {
        dimension: 'Cost model',
        raisyLevel: 'partial',
        competitorLevel: 'partial',
        raisy:
          'Subscription for a managed workforce — priced against hiring',
        competitor:
          'Free open-source framework — plus the engineering salaries to build and run on it',
      },
      {
        dimension: 'Best for',
        raisyLevel: 'yes',
        competitorLevel: 'yes',
        raisy:
          'Companies that want AI to run sales, recruiting, fundraising, or operations with oversight',
        competitor:
          'Engineering teams building bespoke agent products who want full code control',
      },
    ],
    deepDives: [
      {
        icon: LayoutGrid,
        title: 'An org chart, not a codebase',
        raisy:
          'Raisy models your company as Entities, Processes, Bots, and Kernels — a living, visual management structure. You manage the workforce; you don\'t program it.',
        competitor:
          'crewAI models agents, tasks, and crews in Python code. Elegant for engineers — but "what is my AI workforce doing" is answered by reading code and logs, not by opening a management UI.',
      },
      {
        icon: Eye,
        title: 'Radical transparency: no invisible LLM calls',
        raisy:
          'Every LLM call in Raisy is logged with exact kernel versions, full input, output, and token usage — reproducible down to an assembly hash. Auditability is the design, not an afterthought.',
        competitor:
          'Frameworks give you hooks to add observability (and good third-party tools exist), but out of the box, agent reasoning is a black box you must instrument yourself.',
      },
      {
        icon: Gauge,
        title: 'KPIs that quantify every entity, process, and bot',
        raisy:
          'Raisy computes KPIs at every level with deterministic code and uses approval rates to graduate bots from supervised to autonomous — a trust model built into the platform.',
        competitor:
          'crewAI has no concept of per-agent business KPIs or earned autonomy. If you want agents that earn trust through measured performance, you are building that system — which is exactly what Raisy already is.',
      },
      {
        icon: GitBranch,
        title: 'Self-learning transparency — without forgetting',
        raisy:
          'Rejections trigger kernel patches that are quality-gated and stored as new immutable versions. One correction improves every bot sharing that capability, and nothing is ever overwritten.',
        competitor:
          'Improving a crewAI agent means editing prompts or code and redeploying. No versioned improvement trail, no guarantee yesterday\'s correction survives today\'s refactor.',
      },
      {
        icon: Rocket,
        title: 'Setup made easy: from one URL to a running workforce',
        raisy:
          'Onboarding starts with your company website. Raisy drafts the entity, proposes the processes, assembles the bots. You review — no repo, no Python, no deployment pipeline.',
        competitor:
          'crewAI setup is a software project: environment, code, prompts, tools, tests, deployment, monitoring. The right investment if agents are your product; a detour if they\'re your workforce.',
      },
    ],
    competitorGoodFit: [
      'You have Python engineers and want full code-level control of your agents',
      'You are building an AI product where agents are the core IP',
      'You want free, open-source building blocks and accept building everything around them',
      'You enjoy designing agent architectures and want no platform opinions imposed',
    ],
    raisyGoodFit: [
      'You want business outcomes, not an agent engineering project',
      'You need governance: approval gates, audit trails, and KPIs for every AI action',
      'You want the system to learn from corrections without losing history',
      'You want a management UI for your AI workforce, not a codebase',
      'You\'d compare the cost to hiring employees, not to an open-source library',
    ],
    faqs: [
      {
        question: 'Is Raisy built on crewAI or LangChain?',
        answer:
          'No. Raisy is its own governed runtime (Python/FastAPI) with its own kernel-bot-process-entity architecture. It is designed to ingest agent runtimes and skill ecosystems as plugins — frameworks like crewAI are potential suppliers to Raisy, not the other way around.',
      },
      {
        question: 'crewAI is free. Why pay for Raisy?',
        answer:
          'The framework is free; the workforce isn\'t. With crewAI you pay in engineering time for orchestration, UI, governance, KPIs, and maintenance — typically far more than a Raisy subscription. Raisy is priced against hiring people, not against open-source libraries.',
      },
      {
        question: 'Can Raisy run crewAI-style agents or skills?',
        answer:
          'Yes — Raisy\'s skill layer is designed to ingest external skill formats (including OpenClaw/Claude-style skills) and run them in isolated environments, wrapped with Raisy\'s KPIs, approval gates, and audit logging.',
      },
      {
        question: 'What if I already built agents with crewAI?',
        answer:
          'You can bring the valuable parts — prompts, tools, task definitions — into Raisy as kernels and skills, gaining the management layer: versioning, KPIs, oversight, and memory. Think of it as promoting your prototype into a governed workforce.',
      },
      {
        question: 'Which gives me more control?',
        answer:
          'crewAI gives more code control; Raisy gives more business control. If "control" means changing any line of agent logic, pick a framework. If it means approving every external action, auditing every decision, and quantifying every agent\'s performance — that\'s Raisy.',
      },
    ],
    lastUpdated: 'July 28, 2026',
  },
];

export const getComparisonBySlug = (slug: string): Comparison | undefined =>
  comparisons.find((c) => c.slug === slug);
